/**
 * Makes sure a fading Swiper always finishes its transition.
 *
 * A fade slider never moves its wrapper, so Swiper fakes the wrapper's
 * `transitionend` from a one-shot listener it puts on the slide itself
 * (`effectVirtualTransitionEnd`). That listener bails out on events it did not
 * expect -- but `{ once: true }` has already dropped it by then, so a
 * `transitionend` bubbling up from anything *inside* the slide (parallax
 * children, a button that happens to be hovered) eats it.
 *
 * When that happens the wrapper never gets its event: `swiper.animating` stays
 * true forever, autoplay waits for a transition that never reports back, and
 * `slideNext()`/`slidePrev()` refuse to run because of `loopPreventsSliding`.
 * The slider ends up frozen on whichever slide it reached.
 *
 * So we watch the slides ourselves, ignoring anything that merely bubbled
 * through them, and end the transition on Swiper's behalf if it has not managed
 * to. This deliberately fires at the same moment Swiper's own listener would
 * have, so autoplay keeps its normal rhythm.
 *
 * @param {Object} swiper an initialised Swiper instance
 */
export default function swiperTransitionGuard (swiper) {
    const endTransition = () => {
        if (swiper.destroyed || !swiper.animating || !swiper.wrapperEl) {
            return
        }

        swiper.wrapperEl.dispatchEvent(new CustomEvent('transitionend', {
            bubbles: true,
            cancelable: true
        }))
    }

    swiper.slides.forEach(slide => {
        slide.addEventListener('transitionend', event => {
            // Only the slide's own fade tells us the transition is over --
            // anything else reaching us came from a child and bubbled through.
            if (event.target === slide && event.propertyName === 'opacity') {
                endTransition()
            }
        })
    })

    // A slide that fades from 0 to 0 never transitions at all, so keep a timer
    // as a last resort. It is late on purpose: it must not beat the fade.
    let timeout

    const clear = () => clearTimeout(timeout)

    swiper.on('transitionStart', () => {
        clear()
        timeout = setTimeout(endTransition, swiper.params.speed + 200)
    })

    swiper.on('transitionEnd', clear)
    swiper.on('destroy', clear)
}
